import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { APPLICATION_STATUS, FIRM_CERTIFICATE_ATTENTION_FILTERS } from '@shared/utils/app-static-data';
import { IAccountAffiliation } from 'app/models/common/account-affiliation';
import { IPersonContactInformation } from 'app/models/people/personContactInformation';
import { CommonService } from 'app/services/common/common.service';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { IFirm } from 'app/models/firms/firm';
import { IFirmAddress } from 'app/models/firms/firm-address';
import { IFirmApplication } from 'app/models/firms/firm-application';
import { IFirmApplicationDeficiency } from 'app/models/firms/firm-application-deficiency';
import { IFirmApplicationDocument } from 'app/models/firms/firm-application-document';
import { IFirmApplicationPerson } from 'app/models/firms/firm-application-person';
import { IFirmCertificate } from 'app/models/firms/firm-certificate';
import { IFirmContactInformation } from 'app/models/firms/firm-contact-information';
import { IFirmCredential } from 'app/models/firms/firm-credential';
import { IFirmDashboardData } from 'app/models/firms/firm-dashboard';
import { IFirmFilter } from 'app/models/firms/firm-filter';
import { IFirmPerson } from 'app/models/firms/firm-person';
import { IFirmWorkspace } from 'app/models/firms/firm-workspace';
import { IDocumentFile } from 'app/models/document/document-file';

interface ILegacyFirmApplicationDto {
  id: number;
  userId?: string;
  userID?: string;
  approverEmail?: string;
  applicationViableDate?: string;
  lastUpdated?: string;
  firm?: any;
  status?: string;
  licenseDate?: string;
  applicationDate?: string;
  licenseType?: string;
  applicationType?: string;
  firmName?: string;
  ccb?: string;
  coverage?: string;
  renewal?: boolean;
  isPublic?: boolean;
  firmServices?: string;
  approvalDate?: string;
  previousApplication?: string;
  inspections?: boolean;
  clearanceTesting?: boolean;
  leadHazardScreening?: boolean;
  riskAssessments?: boolean;
  abatement?: boolean;
  mailingIsPhysical?: boolean;
  contactTitle?: string;
  contactFirstName?: string;
  contactMiddleName?: string;
  contactLastName?: string;
  contactPhone?: string;
  contactAlternatePhone?: string;
  contactFaxPhone?: string;
  contactEmail?: string;
  contactConfirmEmail?: string;
  firmPhysicalStreet?: string;
  firmPhysicalState?: string;
  firmPhysicalCity?: string;
  firmPhysicalZip?: string;
  firmPhysicalCounty?: string;
  firmMailingStreet?: string;
  firmMailingState?: string;
  firmMailingCity?: string;
  firmMailingZip?: string;
  firmMailingCounty?: string;
  firmPhone?: string;
  firmAlternatePhone?: string;
  firmFaxPhone?: string;
  firmEmail?: string;
  firmConfirmEmail?: string;
  firmWebsite?: string;
  attestation?: boolean;
  esignature?: string;
  deficiencies?: ILegacyFirmApplicationDeficiencyDto[];
}

interface ILegacyFirmApplicationDeficiencyDto {
  id: number;
  applicationId: number;
  description?: string;
  isResolved?: boolean;
}

interface ILegacyFirmApplicationPersonDto {
  id: number;
  applicationId: number;
  person?: ILegacyPersonDto;
  firstName?: string;
  lastName?: string;
  certificateId?: string;
  discipline?: string;
  atp?: string;
  trainingDate?: string;
  firmIsPaying?: boolean;
}

interface ILegacyPersonDto {
  id: number;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  title?: string;
  ssn?: string;
  dateOfBirth?: string | null;
  inactive?: boolean;
  response?: string;
  certificates?: any[] | null;
  affiliations?: string[] | null;
}

interface ILegacyFirmApplicationDocumentDto {
  id: number;
  applicationId: number;
  document: IDocumentFile;
  fileName?: string;
  fileType?: string;
  fileDescription?: string;
  applicationPersonId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class FirmService {
  private readonly api = environment.privateApi;
  private static readonly expiringWindowDays = 180;

  constructor(private http: HttpClient, private commonService: CommonService) {}

  getFirmDashboardData(): Observable<IFirmDashboardData> {
    return forkJoin({
      firms: this.getUserFirms({
        filterString: '',
        filterStatus: 'Both',
        expirationStartDate: null,
        expirationEndDate: null,
        certificateStatuses: [],
      }),
      applications: this.getUserFirmApplications(),
    }).pipe(
      map(({ firms, applications }) => {
        const openApplications = applications.filter((application) => this.isVisibleDashboardApplication(application.applicationStatus));

        return {
          summary: this.buildDashboardSummary(firms, applications, openApplications),
          firms,
          applications: openApplications,
        };
      }),
    );
  }

  getUserFirms(filter: IFirmFilter): Observable<IFirm[]> {
    return this.commonService.getAccountAffiliation().pipe(
      map((affiliations) => this.mapAffiliatedFirms(affiliations)),
      switchMap((firms) => this.enrichWithCertificates(firms)),
      map((firms) => this.applyFirmFilter(firms, filter)),
    );
  }

  getFirm(id: number): Observable<IFirm> {
    return this.http.get<any>(this.api + 'Firm/GetFirm/' + id, { withCredentials: true }).pipe(
      map((firm) => this.mapFirm(firm)),
    );
  }

  saveFirm(firm: IFirm): Observable<IFirm> {
    return this.http.post<any>(
      this.api + 'Firm/SaveFirm',
      this.toFirmDto(firm),
      { withCredentials: true },
    ).pipe(
      map((savedFirm) => this.mapFirm(savedFirm)),
    );
  }

  getFirmAddresses(firmId: number): Observable<IFirmAddress[]> {
    return this.http.get<any[]>(this.api + 'Firm/GetFirmAddresses/' + firmId, { withCredentials: true }).pipe(
      map((addresses) => (addresses || []).map((address) => ({
        id: address.id,
        firmId: address.firm?.id ?? firmId,
        address: address.address,
        isCurrentPhysical: address.isCurrentPhysical === true,
        isCurrentMailing: address.isCurrentMailing === true,
      }))),
    );
  }

  saveFirmAddress(firmAddress: IFirmAddress): Observable<IFirmAddress> {
    return this.http.post<any>(
      this.api + 'Firm/SaveFirmAddress',
      {
        id: firmAddress.id,
        firm: this.toFirmDto({ id: firmAddress.firmId } as IFirm),
        address: firmAddress.address,
        isCurrentPhysical: firmAddress.isCurrentPhysical === true,
        isCurrentMailing: firmAddress.isCurrentMailing === true,
      },
      { withCredentials: true },
    ).pipe(
      map((address) => ({
        id: address.id,
        firmId: address.firm?.id ?? firmAddress.firmId,
        address: address.address,
        isCurrentPhysical: address.isCurrentPhysical === true,
        isCurrentMailing: address.isCurrentMailing === true,
      })),
    );
  }

  deleteFirmAddress(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmAddress/' + id, { withCredentials: true });
  }

  getFirmContactInformation(firmId: number): Observable<IFirmContactInformation[]> {
    return this.http.get<any[]>(this.api + 'Firm/GetFirmContactInformation/' + firmId, { withCredentials: true }).pipe(
      map((contacts) => (contacts || []).map((contact) => ({
        id: contact.id,
        firmId: contact.firm?.id ?? firmId,
        contactInformation: contact.contactInformation,
      }))),
    );
  }

  saveFirmContactInformation(contact: IFirmContactInformation): Observable<IFirmContactInformation> {
    return this.http.post<any>(
      this.api + 'Firm/SaveFirmContactInformation',
      {
        id: contact.id,
        firm: this.toFirmDto({ id: contact.firmId } as IFirm),
        contactInformation: contact.contactInformation,
      },
      { withCredentials: true },
    ).pipe(
      map((savedContact) => ({
        id: savedContact.id,
        firmId: savedContact.firm?.id ?? contact.firmId,
        contactInformation: savedContact.contactInformation,
      })),
    );
  }

  deleteFirmContactInformation(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmContactInformation/' + id, { withCredentials: true });
  }

  getFirmPeople(firmId: number): Observable<IFirmPerson[]> {
    return this.http.get<any[]>(this.api + 'Firm/GetFirmPeopleAndContactInformation/' + firmId, { withCredentials: true }).pipe(
      map((people) => (people || []).map((personContact) => ({
        id: personContact.id,
        firmId,
        person: personContact.person,
        contactInformation: personContact.contactInformation
          ?? ((personContact.person?.preferredEmail || '').trim().length
            ? {
              id: 0,
              contactType: 'Work',
              phone: '',
              ext: '',
              cell: '',
              fax: '',
              email: personContact.person.preferredEmail,
              website: '',
            }
            : null),
        isPoc: personContact.isPoc === true || personContact.isPOC === true,
        requestStatus: personContact.requestStatus || null,
        isRequestOnly: personContact.isRequestOnly === true,
      }))),
    );
  }

  saveFirmPerson(firmPerson: IFirmPerson): Observable<IFirmPerson> {
    return this.http.post<any>(
      this.api + 'Firm/SaveFirmPerson',
      {
        id: firmPerson.id,
        person: firmPerson.person,
        firm: this.toFirmDto({ id: firmPerson.firmId } as IFirm),
        application: null,
        isPOC: firmPerson.isPoc === true,
      },
      { withCredentials: true },
    ).pipe(
      map((savedPerson) => ({
        id: savedPerson.id,
        firmId: savedPerson.firm?.id ?? firmPerson.firmId,
        person: savedPerson.person,
        contactInformation: savedPerson.contactInformation ?? firmPerson.contactInformation ?? null,
        isPoc: savedPerson.isPOC === true || savedPerson.isPoc === true,
        requestStatus: savedPerson.requestStatus || null,
        isRequestOnly: savedPerson.isRequestOnly === true,
      })),
    );
  }

  deleteFirmPerson(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmPerson/' + id, { withCredentials: true });
  }

  requestFirmStaffAffiliation(firmId: number, personId: number): Observable<any> {
    return this.http.post(
      this.api + 'Firm/RequestFirmStaffAffiliation',
      {
        personId,
        entityType: 'Firm',
        entityId: firmId,
      },
      { withCredentials: true },
    );
  }

  cancelFirmStaffAffiliationRequest(firmId: number, personId: number): Observable<any> {
    return this.http.post(
      this.api + 'Firm/CancelFirmStaffAffiliationRequest',
      {
        personId,
        entityType: 'Firm',
        entityId: firmId,
      },
      { withCredentials: true },
    );
  }

  getFirmCertificates(firmId: number): Observable<IFirmCertificate[]> {
    return this.http.get<any[]>(this.api + 'Firm/AllFirmCertificates/' + firmId, { withCredentials: true }).pipe(
      map((certificates) => (certificates || []).map((certificate) => this.mapCertificate(certificate))),
    );
  }

  getFirmCredentials(firmId: number): Observable<IFirmCredential[]> {
    return this.http.get<any[]>(this.api + 'Firm/AllFirmLeadCredentials/' + firmId, { withCredentials: true }).pipe(
      map((credentials) => (credentials || []).map((credential) => ({
        id: credential.id,
        firmLeadCredentialType: credential.firmLeadCredentialType,
      }))),
    );
  }

  saveFirmCredential(firmId: number, credential: IFirmCredential): Observable<IFirmCredential> {
    return this.http.post<any>(
      this.api + 'Firm/SaveFirmLeadCredential',
      {
        id: credential.id,
        firm: this.toFirmDto({ id: firmId } as IFirm),
        firmLeadCredentialType: credential.firmLeadCredentialType,
      },
      { withCredentials: true },
    ).pipe(
      map((savedCredential) => ({
        id: savedCredential.id,
        firmLeadCredentialType: savedCredential.firmLeadCredentialType,
      })),
    );
  }

  deleteFirmCredential(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmLeadCredential/' + id, { withCredentials: true });
  }

  getUserFirmApplications(): Observable<IFirmApplication[]> {
    return this.http.get<ILegacyFirmApplicationDto[]>(this.api + 'Firm/GetFirmApplications/', { withCredentials: true }).pipe(
      map((applications) => (applications || [])
        .map((application) => this.mapApplication(application))
        .sort((left, right) => this.toSortDate(right.applicationDate) - this.toSortDate(left.applicationDate))),
    );
  }

  getFirmApplicationById(id: number): Observable<IFirmApplication> {
    return this.http.get<ILegacyFirmApplicationDto>(this.api + 'Firm/GetFirmApplicationById/' + id, { withCredentials: true }).pipe(
      map((application) => this.mapApplication(application)),
    );
  }

  saveFirmApplication(application: IFirmApplication): Observable<IFirmApplication> {
    return this.http.post<ILegacyFirmApplicationDto>(
      this.api + 'Firm/SaveFirmApplication',
      this.toApplicationDto(application),
      { withCredentials: true },
    ).pipe(
      map((savedApplication) => this.mapApplication(savedApplication)),
    );
  }

  getFirmApplicationPeople(applicationId: number): Observable<IFirmApplicationPerson[]> {
    return this.http.get<ILegacyFirmApplicationPersonDto[]>(this.api + 'Firm/GetFirmApplicationPeople/' + applicationId, { withCredentials: true }).pipe(
      map((people) => (people || []).map((person) => this.mapApplicationPerson(person))),
    );
  }

  saveFirmApplicationPerson(person: IFirmApplicationPerson): Observable<IFirmApplicationPerson> {
    return this.http.post<ILegacyFirmApplicationPersonDto>(
      this.api + 'Firm/SaveFirmApplicationPerson',
      this.toApplicationPersonDto(person),
      { withCredentials: true },
    ).pipe(
      map((savedPerson) => this.mapApplicationPerson(savedPerson)),
    );
  }

  deleteFirmApplicationPerson(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmApplicationPerson/' + id, { withCredentials: true });
  }

  getFirmApplicationDocuments(applicationId: number): Observable<IFirmApplicationDocument[]> {
    return this.http.get<ILegacyFirmApplicationDocumentDto[]>(this.api + 'Firm/GetFirmApplicationDocuments/' + applicationId, { withCredentials: true }).pipe(
      map((documents) => (documents || []).map((document) => this.mapApplicationDocument(document))),
    );
  }

  saveFirmApplicationDocument(document: IFirmApplicationDocument): Observable<IFirmApplicationDocument> {
    return this.http.post<ILegacyFirmApplicationDocumentDto>(
      this.api + 'Firm/SaveFirmApplicationDocument',
      this.toApplicationDocumentDto(document),
      { withCredentials: true },
    ).pipe(
      map((savedDocument) => this.mapApplicationDocument(savedDocument)),
    );
  }

  deleteFirmApplicationDocument(id: number): Observable<any> {
    return this.http.get(this.api + 'Firm/DeleteFirmApplicationDocument/' + id, { withCredentials: true });
  }

  searchFirmNames(searchTerm: string): Observable<Array<{ name: string; isPrivate: boolean }>> {
    return this.http.get<Array<{ name: string; isPrivate: boolean }>>(
      this.api + 'Firm/GetSimilarFirmNames/' + encodeURIComponent(searchTerm),
      { withCredentials: true },
    );
  }

  getAllFirmNames(): Observable<Array<{ id: number; name: string; isPrivate: boolean }>> {
    return this.http.get<any[]>(this.api + 'Firm/GetAllFirmNames', { withCredentials: true }).pipe(
      map((firms) => (firms || []).map((firm) => ({
        id: firm.id,
        name: firm.name,
        isPrivate: firm.isPrivate === true,
      }))),
    );
  }

  getFirmByName(firmName: string): Observable<IFirm | null> {
    return this.http.get<any>(this.api + 'Firm/GetFirmByName/' + encodeURIComponent(firmName), { withCredentials: true }).pipe(
      map((firm) => (firm ? this.mapFirm(firm) : null)),
    );
  }

  getFirmApplications(firmId?: number | null): Observable<IFirmApplication[]> {
    return this.getUserFirmApplications().pipe(
      map((applications) => {
        if (!firmId) {
          return applications;
        }

        return applications.filter((application) => application.firmId === firmId);
      }),
    );
  }

  getUnresolvedApplicationDeficiencyDescriptions(application: IFirmApplication | null | undefined): string[] {
    return (application?.deficiencies || [])
      .filter((deficiency) => deficiency.isResolved !== true)
      .map((deficiency) => (deficiency.description || '').trim())
      .filter((description) => description.length > 0);
  }

  getUnresolvedApplicationDeficiencyText(application: IFirmApplication | null | undefined): string {
    return this.getUnresolvedApplicationDeficiencyDescriptions(application).join('\n\n');
  }

  shouldDisplayApplicationDeficiencies(application: IFirmApplication | null | undefined): boolean {
    return application?.applicationStatus === APPLICATION_STATUS.actionNeeded
      && this.getUnresolvedApplicationDeficiencyDescriptions(application).length > 0;
  }

  getFirmWorkspace(firmId: number): Observable<IFirmWorkspace> {
    return forkJoin({
      firm: this.getFirm(firmId),
      addresses: this.getFirmAddresses(firmId),
      contacts: this.getFirmContactInformation(firmId),
      people: this.getFirmPeople(firmId),
      certificates: this.getFirmCertificates(firmId),
      credentials: this.getFirmCredentials(firmId),
      applications: this.getFirmApplications(firmId),
      linkedUsers: this.getFirmLinkedUsers(firmId),
    }).pipe(
      map((workspace) => ({
        ...workspace,
        firm: this.withCertificateSummary(workspace.firm, workspace.certificates),
      })),
    );
  }

  private getFirmLinkedUsers(firmId: number): Observable<IAccountAffiliation[]> {
    return this.commonService.getAllFirmAccountAffiliations().pipe(
      map((affiliations) => (affiliations || [])
        .filter((affiliation) => affiliation.firm?.id === firmId)
        .sort((left, right) => (left.userName || '').localeCompare(right.userName || ''))),
    );
  }

  private mapAffiliatedFirms(affiliations: IAccountAffiliation[]): IFirm[] {
    const firmsById = new Map<number, IFirm>();

    (affiliations || []).forEach((affiliation) => {
      if (!affiliation?.firm || affiliation.status === 'Cancelled') {
        return;
      }

      if (!firmsById.has(affiliation.firm.id)) {
        firmsById.set(affiliation.firm.id, this.mapFirm(affiliation.firm));
      }
    });

    return Array.from(firmsById.values()).sort((left, right) => left.name.localeCompare(right.name));
  }

  private enrichWithCertificates(firms: IFirm[]): Observable<IFirm[]> {
    if (!firms.length) {
      return of([]);
    }

    return forkJoin(
      firms.map((firm) => this.getFirmCertificates(firm.id).pipe(
        map((certificates) => this.withCertificateSummary(firm, certificates)),
        catchError(() => of(this.withCertificateSummary(firm, []))),
      ))
    );
  }

  private applyFirmFilter(firms: IFirm[], filter: IFirmFilter): IFirm[] {
    return firms.filter((firm) => {
      const text = (filter.filterString || '').trim().toLowerCase();
      const matchesText = !text || [
        firm.id.toString(),
        firm.name,
        firm.ccb || '',
        firm.servicesAvailable || '',
      ].join(' ').toLowerCase().includes(text);

      const status = (filter.filterStatus || '').toLowerCase();
      const matchesStatus = !status || status === 'both' || (status === 'active' ? !firm.inactive : firm.inactive);

      const relevantDates = [firm.ccbExpirationDate, firm.nextCertificateExpirationDate]
        .filter((value): value is string => !!value)
        .map((value) => this.toDateOnly(value));

      const startDate = filter.expirationStartDate ? this.toDateOnly(filter.expirationStartDate) : null;
      const endDate = filter.expirationEndDate ? this.toDateOnly(filter.expirationEndDate) : null;
      const matchesDate = this.matchesDateRange(relevantDates, startDate, endDate);

      const certificateStatuses = filter.certificateStatuses || [];
      const matchesCertificateAttention = !certificateStatuses.length || certificateStatuses.some((certificateStatus) => {
        if (certificateStatus === FIRM_CERTIFICATE_ATTENTION_FILTERS.expired) {
          return (firm.certificateSummary?.expired ?? 0) > 0;
        }

        if (certificateStatus === FIRM_CERTIFICATE_ATTENTION_FILTERS.expiring) {
          return (firm.certificateSummary?.expiring ?? 0) > 0;
        }

        return false;
      });

      return matchesText && matchesStatus && matchesDate && matchesCertificateAttention;
    });
  }

  private matchesDateRange(dates: Date[], startDate: Date | null, endDate: Date | null): boolean {
    if (!startDate && !endDate) {
      return true;
    }

    if (!dates.length) {
      return false;
    }

    return dates.some((date) => {
      const afterStart = !startDate || date >= startDate;
      const beforeEnd = !endDate || date <= endDate;
      return afterStart && beforeEnd;
    });
  }

  private mapFirm(firm: any): IFirm {
    return {
      id: firm.id,
      name: firm.name,
      inactive: firm.inactive === true,
      isPrivate: firm.isPrivate === true,
      ccb: firm.ccb || null,
      ccbExpirationDate: firm.ccbExpirationDate || null,
      propertyManagementLicense: firm.propertyManagementLicense || null,
      servicesAvailable: firm.servicesAvailable || null,
      coverage: firm.coverage || null,
      certificationNumber: firm.certificationNumber || null,
      nextCertificateExpirationDate: null,
      certificateSummary: null,
    };
  }

  private toFirmDto(firm: IFirm | null | undefined): any {
    return {
      id: firm?.id ?? 0,
      name: firm?.name ?? null,
      inactive: firm?.inactive === true,
      isPrivate: firm?.isPrivate === true,
      ccb: firm?.ccb ?? null,
      ccbExpirationDate: firm?.ccbExpirationDate ?? null,
      propertyManagementLicense: firm?.propertyManagementLicense ?? null,
      servicesAvailable: firm?.servicesAvailable ?? null,
      certificationNumber: firm?.certificationNumber ?? null,
      coverage: firm?.coverage ?? null,
    };
  }

  private mapCertificate(certificate: any): IFirmCertificate {
    return {
      id: certificate.id,
      codeType: certificate.codeType,
      stage: certificate.stage || null,
      issueDate: certificate.issueDate || null,
      expirationDate: certificate.expirationDate || null,
      controlNumber: certificate.controlNumber || null,
      certificateNumber: certificate.certificateNumber || null,
      dateOfLetter: certificate.dateOfLetter || null,
    };
  }

  private mapApplication(application: ILegacyFirmApplicationDto): IFirmApplication {
    const firm = application.firm ? this.mapFirm(application.firm) : null;

    return {
      id: application.id,
      userId: application.userId ?? application.userID ?? null,
      firmId: firm?.id ?? null,
      firm,
      approverEmail: application.approverEmail ?? null,
      applicationViableDate: application.applicationViableDate ?? null,
      firmName: application.firmName ?? firm?.name ?? 'Unlinked Firm Application',
      ccb: application.ccb ?? firm?.ccb ?? null,
      coverage: application.coverage ?? null,
      applicationStatus: application.status ?? 'Unknown',
      applicationDate: application.applicationDate ?? application.licenseDate ?? null,
      approvalDate: application.approvalDate ?? null,
      lastUpdated: application.lastUpdated ?? null,
      applicationType: application.applicationType ?? application.licenseType ?? '',
      renewal: application.renewal === true,
      previousApplication: application.previousApplication ?? null,
      isPublic: application.isPublic ?? null,
      firmServices: application.firmServices ?? firm?.servicesAvailable ?? null,
      inspections: application.inspections === true,
      clearanceTesting: application.clearanceTesting === true,
      leadHazardScreening: application.leadHazardScreening === true,
      riskAssessments: application.riskAssessments === true,
      abatement: application.abatement === true,
      mailingIsPhysical: application.mailingIsPhysical === true,
      contactTitle: application.contactTitle ?? null,
      contactFirstName: application.contactFirstName ?? null,
      contactMiddleName: application.contactMiddleName ?? null,
      contactLastName: application.contactLastName ?? null,
      contactPhone: application.contactPhone ?? null,
      contactAlternatePhone: application.contactAlternatePhone ?? null,
      contactFaxPhone: application.contactFaxPhone ?? null,
      contactEmail: application.contactEmail ?? null,
      contactConfirmEmail: application.contactConfirmEmail ?? null,
      firmPhysicalStreet: application.firmPhysicalStreet ?? null,
      firmPhysicalState: application.firmPhysicalState ?? null,
      firmPhysicalCity: application.firmPhysicalCity ?? null,
      firmPhysicalZip: application.firmPhysicalZip ?? null,
      firmPhysicalCounty: application.firmPhysicalCounty ?? null,
      firmMailingStreet: application.firmMailingStreet ?? null,
      firmMailingState: application.firmMailingState ?? null,
      firmMailingCity: application.firmMailingCity ?? null,
      firmMailingZip: application.firmMailingZip ?? null,
      firmMailingCounty: application.firmMailingCounty ?? null,
      firmPhone: application.firmPhone ?? null,
      firmAlternatePhone: application.firmAlternatePhone ?? null,
      firmFaxPhone: application.firmFaxPhone ?? null,
      firmEmail: application.firmEmail ?? null,
      firmConfirmEmail: application.firmConfirmEmail ?? null,
      firmWebsite: application.firmWebsite ?? null,
      attestation: application.attestation === true,
      esignature: application.esignature ?? null,
      deficiencies: (application.deficiencies || []).map((deficiency) => this.mapApplicationDeficiency(deficiency)),
    };
  }

  private mapApplicationDeficiency(deficiency: ILegacyFirmApplicationDeficiencyDto): IFirmApplicationDeficiency {
    return {
      id: deficiency.id,
      applicationId: deficiency.applicationId,
      description: deficiency.description ?? '',
      isResolved: deficiency.isResolved === true,
    };
  }

  private mapApplicationPerson(person: ILegacyFirmApplicationPersonDto): IFirmApplicationPerson {
    return {
      id: person.id,
      applicationId: person.applicationId,
      person: this.mapPerson(person.person),
      firstName: person.firstName ?? '',
      lastName: person.lastName ?? '',
      certificateId: person.certificateId ?? null,
      discipline: person.discipline ?? null,
      atp: person.atp ?? null,
      trainingDate: person.trainingDate ?? null,
      firmIsPaying: person.firmIsPaying === true,
    };
  }

  private mapPerson(person: ILegacyPersonDto | undefined): IFirmApplicationPerson['person'] {
    if (!person) {
      return null;
    }

    return {
      id: person.id,
      firstName: person.firstName ?? '',
      lastName: person.lastName ?? '',
      middleName: person.middleName ?? '',
      title: person.title ?? '',
      ssn: person.ssn ?? '',
      dateOfBirth: person.dateOfBirth ?? null,
      inactive: person.inactive === true,
      response: person.response ?? '',
      certificates: null,
      affiliations: person.affiliations ?? null,
    };
  }

  private mapApplicationDocument(document: ILegacyFirmApplicationDocumentDto): IFirmApplicationDocument {
    return {
      id: document.id,
      applicationId: document.applicationId,
      document: document.document,
      fileName: document.fileName ?? '',
      fileType: document.fileType ?? '',
      fileDescription: document.fileDescription ?? null,
      applicationPersonId: document.applicationPersonId ?? null,
    };
  }

  private toApplicationDto(application: IFirmApplication): ILegacyFirmApplicationDto {
    return {
      id: application.id,
      userID: application.userId ?? undefined,
      approverEmail: application.approverEmail ?? undefined,
      applicationViableDate: application.applicationViableDate ?? undefined,
      firm: application.firmId ? { id: application.firmId } : application.firm ? { id: application.firm.id } : null,
      status: application.applicationStatus,
      applicationDate: application.applicationDate ?? new Date().toISOString(),
      applicationType: application.applicationType,
      firmName: application.firmName,
      ccb: application.ccb ?? undefined,
      coverage: application.coverage ?? undefined,
      renewal: application.renewal,
      previousApplication: application.previousApplication ?? undefined,
      isPublic: application.isPublic ?? undefined,
      firmServices: application.firmServices ?? undefined,
      approvalDate: application.approvalDate ?? undefined,
      inspections: application.inspections,
      clearanceTesting: application.clearanceTesting,
      leadHazardScreening: application.leadHazardScreening,
      riskAssessments: application.riskAssessments,
      abatement: application.abatement,
      mailingIsPhysical: application.mailingIsPhysical,
      contactTitle: application.contactTitle ?? undefined,
      contactFirstName: application.contactFirstName ?? undefined,
      contactMiddleName: application.contactMiddleName ?? undefined,
      contactLastName: application.contactLastName ?? undefined,
      contactPhone: application.contactPhone ?? undefined,
      contactAlternatePhone: application.contactAlternatePhone ?? undefined,
      contactFaxPhone: application.contactFaxPhone ?? undefined,
      contactEmail: application.contactEmail ?? undefined,
      contactConfirmEmail: application.contactConfirmEmail ?? undefined,
      firmPhysicalStreet: application.firmPhysicalStreet ?? undefined,
      firmPhysicalState: application.firmPhysicalState ?? undefined,
      firmPhysicalCity: application.firmPhysicalCity ?? undefined,
      firmPhysicalZip: application.firmPhysicalZip ?? undefined,
      firmPhysicalCounty: application.firmPhysicalCounty ?? undefined,
      firmMailingStreet: application.firmMailingStreet ?? undefined,
      firmMailingState: application.firmMailingState ?? undefined,
      firmMailingCity: application.firmMailingCity ?? undefined,
      firmMailingZip: application.firmMailingZip ?? undefined,
      firmMailingCounty: application.firmMailingCounty ?? undefined,
      firmPhone: application.firmPhone ?? undefined,
      firmAlternatePhone: application.firmAlternatePhone ?? undefined,
      firmFaxPhone: application.firmFaxPhone ?? undefined,
      firmEmail: application.firmEmail ?? undefined,
      firmConfirmEmail: application.firmConfirmEmail ?? undefined,
      firmWebsite: application.firmWebsite ?? undefined,
      attestation: application.attestation,
      esignature: application.esignature ?? undefined,
    };
  }

  private toApplicationPersonDto(person: IFirmApplicationPerson): ILegacyFirmApplicationPersonDto {
    return {
      id: person.id,
      applicationId: person.applicationId,
      person: this.toPersonDto(person.person),
      firstName: person.firstName,
      lastName: person.lastName,
      certificateId: person.certificateId ?? undefined,
      discipline: person.discipline ?? undefined,
      atp: person.atp ?? undefined,
      trainingDate: person.trainingDate ?? undefined,
      firmIsPaying: person.firmIsPaying,
    };
  }

  private toPersonDto(person: IFirmApplicationPerson['person']): ILegacyPersonDto | undefined {
    if (!person) {
      return undefined;
    }

    return {
      id: person.id,
      firstName: person.firstName || undefined,
      middleName: person.middleName || undefined,
      lastName: person.lastName || undefined,
      title: person.title || undefined,
      ssn: person.ssn || undefined,
      dateOfBirth: person.dateOfBirth ?? undefined,
      inactive: person.inactive === true,
      response: person.response || undefined,
      certificates: null,
      affiliations: null,
    };
  }

  private toApplicationDocumentDto(document: IFirmApplicationDocument): ILegacyFirmApplicationDocumentDto {
    return {
      id: document.id,
      applicationId: document.applicationId,
      document: document.document,
      fileName: document.fileName,
      fileType: document.fileType,
      fileDescription: document.fileDescription ?? undefined,
      applicationPersonId: document.applicationPersonId ?? undefined,
    };
  }

  private withCertificateSummary(firm: IFirm, certificates: IFirmCertificate[]): IFirm {
    const today = this.toDateOnly(new Date().toISOString());
    const expiringThreshold = new Date(today);
    expiringThreshold.setDate(expiringThreshold.getDate() + FirmService.expiringWindowDays);

    const validDates = certificates
      .map((certificate) => certificate.expirationDate)
      .filter((value): value is string => !!value)
      .map((value) => this.toDateOnly(value));

    const active = validDates.filter((date) => date >= today).length;
    const expiring = validDates.filter((date) => date >= today && date <= expiringThreshold).length;
    const expired = validDates.filter((date) => date < today).length;
    const nextCertificateExpirationDate = validDates.length
      ? validDates.sort((left, right) => left.getTime() - right.getTime())[0].toISOString()
      : null;

    return {
      ...firm,
      nextCertificateExpirationDate,
      certificateSummary: {
        total: certificates.length,
        active,
        expiring,
        expired,
      },
    };
  }

  private buildDashboardSummary(firms: IFirm[], applications: IFirmApplication[], openApplications: IFirmApplication[]) {
    return {
      affiliatedFirmCount: firms.length,
      activeFirmCount: firms.filter((firm) => !firm.inactive).length,
      applicationCount: applications.length,
      expiringCertificateCount: firms.reduce((total, firm) => total + (firm.certificateSummary?.expiring ?? 0), 0),
      expiredCertificateCount: firms.reduce((total, firm) => total + (firm.certificateSummary?.expired ?? 0), 0),
      openApplicationCount: openApplications.length,
      submittedApplicationCount: applications.filter((application) => application.applicationStatus === APPLICATION_STATUS.submitted || application.applicationStatus === APPLICATION_STATUS.inReview).length,
      actionNeededApplicationCount: applications.filter((application) => application.applicationStatus === APPLICATION_STATUS.actionNeeded).length,
    };
  }

  private isVisibleDashboardApplication(status: string): boolean {
    return status !== APPLICATION_STATUS.cancelled && status !== APPLICATION_STATUS.approved;
  }

  private toDateOnly(value: string): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private toSortDate(value: string | null): number {
    return value ? new Date(value).getTime() : 0;
  }
}